
import React, { useMemo } from 'react';
import { Group, Rect, Text, Line } from 'react-konva';
import { eachDayOfInterval, eachMonthOfInterval, format, isToday, isWeekend } from 'date-fns';
import { SchedulerDimensions } from './schedulerTypes';

interface TimeHeaderProps {
  startDate: Date;
  endDate: Date;
  dimensions: SchedulerDimensions;
  highlightedDayIndex?: number | null;
}

export const TimeHeader = React.memo(function TimeHeader({ 
  startDate, 
  endDate, 
  dimensions, 
  highlightedDayIndex = null 
}: TimeHeaderProps) {
  // Memoize months and days calculations
  const months = useMemo(() => 
    eachMonthOfInterval({ start: startDate, end: endDate }),
    [startDate, endDate]
  );
  
  const days = useMemo(() => 
    eachDayOfInterval({ start: startDate, end: endDate }),
    [startDate, endDate]
  );

  const monthHeight = dimensions.headerHeight / 2;
  const dayHeight = dimensions.headerHeight / 2;
  
  // Memoize month sections data
  const monthSections = useMemo(() => {
    return months.map((month, i) => {
      // Calculate days in this month that are within our view
      const daysInMonth = days.filter(day => (
        day.getMonth() === month.getMonth() && 
        day.getFullYear() === month.getFullYear()
      ));
      
      const startIndex = days.findIndex(day => (
        day.getMonth() === month.getMonth() && 
        day.getFullYear() === month.getFullYear()
      ));
      
      const monthWidth = daysInMonth.length * dimensions.dayWidth;
      const x = startIndex * dimensions.dayWidth;
      
      return {
        month,
        x,
        width: monthWidth,
        monthLabel: format(month, 'MMMM yyyy')
      };
    });
  }, [months, days, dimensions.dayWidth]);

  // Memoize day columns data
  const dayColumns = useMemo(() => {
    return days.map((day, i) => {
      const x = i * dimensions.dayWidth;
      const isCurrentDay = isToday(day);
      const isWeekendDay = isWeekend(day);
      const isHighlighted = highlightedDayIndex === i;
      
      let fillColor = "#ffffff";
      if (isHighlighted) {
        fillColor = "#e5f6ff";
      } else if (isCurrentDay) {
        fillColor = "#e5f6ff";
      } else if (isWeekendDay) {
        fillColor = "#F3F4F6";
      }
      
      return {
        day,
        x,
        fillColor,
        dayNumber: format(day, 'd'),
        dayOfWeek: format(day, 'EEE'),
        isWeekendDay
      };
    });
  }, [days, dimensions.dayWidth, highlightedDayIndex]);
  
  return (
    <Group>
      {/* Header Background */}
      <Rect
        x={0}
        y={0}
        width={days.length * dimensions.dayWidth}
        height={dimensions.headerHeight}
        fill="#ffffff"
        stroke="#e5e7eb"
        strokeWidth={1}
      />

      {/* Month Sections - First */}
      {monthSections.map((section, i) => (
        <Group key={i}>
          {/* Month background */}
          <Rect
            x={section.x}
            y={0}
            width={section.width}
            height={monthHeight}
            fill="#f9fafb"
            stroke="#e5e7eb"
            strokeWidth={1}
          />
          
          {/* Month name */}
          <Text
            x={section.x + 10}
            y={monthHeight / 3}
            text={section.monthLabel}
            fontSize={14}
            fontStyle="bold"
            fontFamily="sans-serif"
          />
        </Group>
      ))}

      {/* Day columns - Below months */}
      {dayColumns.map((column, i) => (
        <Group key={i}>
          {/* Day background */}
          <Rect
            x={column.x}
            y={monthHeight}
            width={dimensions.dayWidth}
            height={dayHeight}
            fill={column.fillColor}
            stroke="#e5e7eb"
            strokeWidth={1}
          />
          
          {/* Day number */}
          <Text
            x={column.x + dimensions.dayWidth / 2 - 8}
            y={monthHeight + dayHeight / 3}
            text={column.dayNumber}
            fontSize={12}
            fontFamily="sans-serif"
            fill={column.isWeekendDay ? "#6B7280" : "#000000"}
          />
          
          {/* Day of week */}
          <Text
            x={column.x + dimensions.dayWidth / 2 - 8}
            y={monthHeight + dayHeight / 1.5}
            text={column.dayOfWeek}
            fontSize={10}
            fontFamily="sans-serif"
            fill={column.isWeekendDay ? "#9CA3AF" : "#6b7280"}
          />
        </Group>
      ))}
      
      {/* Bottom border */}
      <Line
        points={[0, dimensions.headerHeight, days.length * dimensions.dayWidth, dimensions.headerHeight]}
        stroke="#e5e7eb"
        strokeWidth={1}
      />
    </Group>
  );
});
